// src/components/course/TestReview.jsx
import React, { useMemo } from 'react';
import { FiClock, FiTarget, FiActivity, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';

/**
 * Renders a grade box with appropriate styling based on grade
 */
const GradeBox = ({ grade, title, reason, icon }) => {
  // Determine color scheme based on grade
  const colorScheme = useMemo(() => {
    switch (grade) {
      case 'A': return {
        container: 'border-l-green-600',
        badge: 'bg-green-900 text-green-400',
        icon: 'text-green-400'
      };
      case 'B': return {
        container: 'border-l-blue-600',
        badge: 'bg-blue-900 text-blue-400',
        icon: 'text-blue-400'
      };
      case 'C': return {
        container: 'border-l-yellow-600',
        badge: 'bg-yellow-900 text-yellow-400',
        icon: 'text-yellow-400'
      };
      case 'D': return {
        container: 'border-l-red-600',
        badge: 'bg-red-900 text-red-400',
        icon: 'text-red-400'
      };
      default: return {
        container: 'border-l-gray-600',
        badge: 'bg-gray-900 text-gray-400',
        icon: 'text-gray-400'
      };
    }
  }, [grade]);

  return (
    <div className={`bg-[#252525] p-4 rounded-lg border-l-4 ${colorScheme.container} hover:bg-[#2a2a2a] transition-colors mb-4`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center">
          {icon && <span className={`mr-2 ${colorScheme.icon}`}>{icon}</span>}
          <h3 className="text-lg font-bold">{title}</h3>
        </div>
        <div className={`px-3 py-1 rounded-full ${colorScheme.badge}`}>
          Grade: {grade}
        </div>
      </div>
      <p className="text-gray-300">{reason}</p>
    </div>
  );
};

/**
 * A component that displays a list of action items with numbered circles
 */
const ActionPlan = ({ actions }) => (
  <ul className="space-y-2">
    {actions.map((action, index) => (
      <li key={index} className="flex items-start">
        <span className="bg-blue-900 text-blue-300 rounded-full h-6 w-6 flex items-center justify-center mr-2 flex-shrink-0">
          {index + 1}
        </span>
        <span>{action}</span>
      </li>
    ))}
  </ul>
);

/**
 * Component to render problem analysis card
 */
const ProblemCard = ({ problem, index }) => (
  <div className="mb-6 bg-[#252525] p-4 rounded-lg border-l-4 border-red-500">
    <div className="flex items-start mb-4">
      <FiAlertTriangle className="text-red-500 mt-1 mr-3 flex-shrink-0" size={20} />
      <div>
        <h3 className="text-lg font-semibold mb-2">Problem {index + 1}</h3>
        <p className="text-gray-300 mb-4">{problem.stem || problem.problem}</p>
      </div>
    </div>
    
    <div className="bg-blue-900 bg-opacity-20 p-4 rounded-lg border border-blue-700 mb-4">
      <h4 className="text-blue-400 font-semibold mb-2">AI Tutor Advice</h4>
      <p>{problem.aiAdvice}</p>
    </div>
    
    <div className="flex justify-end">
      <button className="text-blue-400 hover:text-blue-300 text-sm flex items-center">
        <span className="mr-1">Review Similar Problems</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12h14"></path>
          <path d="M12 5l7 7-7 7"></path>
        </svg>
      </button>
    </div>
  </div>
);

/**
 * TestReview Component - Displays comprehensive review of user's test performance
 * @param {Object} props - Component props
 * @param {Object} props.introReview - Test review data object
 */
const TestReview = ({ introReview }) => {
  // Show loading state if review data is not available yet
  if (!introReview) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  // Destructure review data
  const { 
    Greeting, 
    "Time Management": timeManagement, 
    QuestionAccuracy: questionAccuracy,
    FocusConsistency: focusConsistency,
    StrategicApproach: strategicApproach,
    WrongProblems: wrongProblems = []
  } = introReview;

  // Recommended action plan steps
  const actionPlanSteps = [
    "Review the problem areas identified in your test results",
    "Complete the recommended practice exercises",
    "Use the AI tutor to get help with concepts you find challenging",
    "Take another practice test to measure your improvement"
  ];
  
  return (
    <div className="prose prose-invert max-w-none">
      {/* Performance Analysis Section */}
      <section className="bg-[#1F1F1F] p-6 rounded-lg mb-8 border border-[#2B2B2B]">
        <h2 className="text-2xl font-bold text-center mb-4">Test Performance Analysis</h2>
        <p className="text-center text-lg mb-6">{Greeting}</p>
        
        {/* Analysis Grid - Time Management and Question Accuracy */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <GradeBox 
            grade={timeManagement.Letter} 
            title="Time Management" 
            reason={timeManagement.Reason}
            icon={<FiClock size={20} />} 
          />
          
          <GradeBox 
            grade={questionAccuracy.Letter} 
            title="Question Accuracy" 
            reason={questionAccuracy.Reason}
            icon={<FiTarget size={20} />} 
          />
        </div>
        
        {/* Analysis Grid - Focus & Consistency and Strategic Approach */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <GradeBox 
            grade={focusConsistency.Letter} 
            title="Focus & Consistency" 
            reason={focusConsistency.Reason}
            icon={<FiActivity size={20} />} 
          />
          
          <GradeBox 
            grade={strategicApproach.Letter} 
            title="Strategic Approach" 
            reason={strategicApproach.Reason}
            icon={<FiCheckCircle size={20} />} 
          />
        </div>
      </section>
      
      {/* Problem Analysis Section - Only show if there are wrong problems */}
      {wrongProblems.length > 0 && (
        <section className="bg-[#1F1F1F] p-6 rounded-lg border border-[#2B2B2B]">
          <h2 className="text-2xl font-bold mb-6">Problem Analysis</h2>
          
          {/* Map through wrong problems */}
          {wrongProblems.map((problem, index) => (
            <ProblemCard key={index} problem={problem} index={index} />
          ))}
          
          {/* Action Plan Section */}
          <div className="bg-[#252525] p-4 rounded-lg mt-8">
            <h3 className="text-lg font-semibold mb-3">Recommended Action Plan</h3>
            <ActionPlan actions={actionPlanSteps} />
          </div>
        </section>
      )}
    </div>
  );
};

export default TestReview;